import { Typography, Box } from "@mui/material"
import { memo, useEffect, useState } from "react";
import {
    getOwners,
    getYearsMatings,
    deleteMating,
} from "../../api/index.js";
import Spinner from "../../components/Spinner.js";
import { useSnackbar } from "../../utils/index.js";
import Form from "../../components/Form.js";
import useSelectStyles from "../../components/styles/Select.js";
import { BackButton } from "../../components/Buttons.js";

const DeleteMating = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [yearsDropdownValues, setYearsDropdownValue] = useState([]);
    const [fetchedMatings, setFetchedMatings] = useState({});
    const [animalMatings, setAnimalMatings] = useState([]);
    const [animalMatingsDatesDropdownValues, setAnimalMatingsDatesDropdownValues] = useState([]);
    const [animalsDropdownValues, setAnimalsDropdownValues] = useState([]);
    const [step, setStep] = useState(1);
    /* Step 1: Select owner and year
      Step 2: Show registration codes of female animals that have been mated during the selected year
      Step 3: Show matings dates of the selected Animal
    */
    const { success, error } = useSnackbar();
    const classes = useSelectStyles();

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);

            const dropdownValues = owners.map((owner) => ({
                value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
            }));
            setOwnersDropdownValues(dropdownValues);
        } catch (err) {
            error(err);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        // Years 2000 to current year
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => ({
            value: (currentYear - i).toString(),
            text: (currentYear - i).toString()
        }));
        setYearsDropdownValue(years);
        fetchData();
    }, []);

    const selectOwnerYearFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Επιλέξτε Εκτροφή",
            required: true,
        },
        {
            customType: "dropdown",
            id: "year",
            items: yearsDropdownValues,
            placeholder: "Επιλέξτε την περίοδο συζεύξεων",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ];

    const selectAnimalFormContent = [
        {
            customType: "dropdown",
            id: "animal",
            items: animalsDropdownValues,
            placeholder: "Επιλέξτε Ζώο",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ];

    const selectedMatingsFormContent = [
        {
            customType: "dropdown",
            id: "matingDate",
            items: animalMatingsDatesDropdownValues,
            placeholder: "Επιλέξτε Ημερομηνία Σύζευξης",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Διαγραφή Σύζευξης"
        },
    ];

    const handleFetchMatings = async (values) => {
        setIsLoading(true);
        const ownerId = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)?._id;
        try {
            const { success: scs, message, matings } = await getYearsMatings(ownerId, values.year);
            if (scs) {
                setFetchedMatings(matings);

                const damRegistrationCodes = Object.keys(matings).filter(code => code !== "Unknown");

                setAnimalsDropdownValues(damRegistrationCodes.map((damCode) => ({
                    value: damCode,
                    text: damCode,
                })));
                setStep(2);
            } else {
                error(message);
            }
        } catch (err) {
            error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const handleAnimalSelect = (values) => {
        const selectedMatings = fetchedMatings[values.animal] || [];

        if (selectedMatings.length > 0) {
            setAnimalMatings(selectedMatings);

            const datesDropdownValues = selectedMatings.map((mating) => ({
                value: new Date(mating?.matingDate1).toLocaleDateString("el-GR"),
                text: new Date(mating?.matingDate1).toLocaleDateString("el-GR"),
            }));

            setAnimalMatingsDatesDropdownValues(datesDropdownValues);
            setStep(3);
        }
    };

    const handleDeleteMating = async (values) => {
        setIsLoading(true);

        const matingToDelete = animalMatings.find((mating) => new Date(mating?.matingDate1).toLocaleDateString("el-GR") === values.matingDate);

        try {
            const { success: scs, message } = await deleteMating(matingToDelete?.id);
            if (scs) {
                success(message);
                setStep(1);
                setAnimalMatings([]);
                setAnimalMatingsDatesDropdownValues([]);
                setAnimalsDropdownValues([]);
                setFetchedMatings({});
            } else {
                error(message);
            }
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            <Box className={classes.outerBox}>
                <Box container flexDirection="column" className={classes.innerBox}>
                    {step === 1 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλέξτε την Εκτροφή την περίοδο συζεύξεων που σας ενδιαφέρει"}
                            </Typography>
                            <Form
                                title="Διαγραφή Συζεύξεων"
                                content={selectOwnerYearFormContent}
                                onSubmit={(values) => handleFetchMatings(values)}
                                toResetForm={true}
                            />
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλέξτε το Ζώο που σας ενδιαφέρει"}
                            </Typography>
                            <Form
                                title="Επιλογή Προβατίνας"
                                content={selectAnimalFormContent}
                                onSubmit={(values) => handleAnimalSelect(values)}
                                toResetForm={true}
                            />
                        </>
                    )}
                    {step === 3 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλέξτε την ημερομηνία σύζευξης που θέλετε να διαγράψετε"}
                            </Typography>
                            <Form
                                title="Επιλογή Σύζευξης"
                                content={selectedMatingsFormContent}
                                onSubmit={(values) => handleDeleteMating(values)}
                                toResetForm={true}
                            />
                        </>
                    )}
                    {step !== 1 && (
                        <BackButton onClick={() => setStep(step - 1)} />
                    )}
                </Box>
            </Box>
        </>
    )
}

export default memo(DeleteMating);